import React, { useContext, useEffect, useState } from 'react';
import 'bootstrap/dist/css/bootstrap.min.css';
import { AuthContext } from "../../../../config/AuthContext";
import { FloatButton, Badge, Modal, Tooltip, Row, Col, Select } from "antd";
import { BiRefresh, BiTrophy } from "react-icons/bi";
import AvaluacioColors from "../../../../styles/AvaluacioColors";
import AppSider from "../../components/AppSider";
import { Content } from "antd/es/layout/layout";
import { motion } from "framer-motion";
import { GetRankingsUsuariId } from "../../../../dao/jutge/GetRankingsUsuariId";
import Mochila from "../../../components/content/Mochila";
import { FaFireAlt, FaStar, FaCrown } from 'react-icons/fa';
import { GetJavamonConfig, getJavamonImage, GetSceneActivities } from '../../../../dao/jutge/GetJavamonConfig';
import { GetUsuarisIdJutgeNotes } from "../../../../dao/GetUsuarisIdJutgeNotes";
import { GetAlumneId } from "../../../../dao/GetAlumneId";

const fallbackRarityColors: Record<string, string> = {
    normal: "#3498db",
    epic: "#f1c40f",
    legendary: "#9b59b6",
};

const bgGradients = {
    champion: 'linear-gradient(135deg, #ffd700 0%, #ff8c00 100%)',
    elite: 'linear-gradient(135deg, #2c3e50 0%, #3498db 100%)',
    challenger: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
    legendary: 'linear-gradient(135deg, #8E2DE2 0%, #4A00E0 100%)' // Gradiente especial morado para usuarios con legendarios
};

const JavamonSprite = ({ jm, size = 50, glow = false, rarityMap = {} }: { jm: any, size?: number, glow?: boolean, rarityMap?: Record<string, string> }) => {
    const isObj = typeof jm === 'object' && jm !== null;
    const img = isObj ? jm.image : jm;
    const rarity = isObj ? jm.rarity : 'normal';
    const nom = isObj ? jm.nom : 'Javamon';
    const finalSrc = isObj ? getJavamonImage(jm) : `/images/javamon/${img ? img.replace(/^\/?images\/javamon\//, "") : ""}`;
    const color = rarityMap[rarity] || rarityMap[rarity?.toLowerCase()] || fallbackRarityColors[rarity] || '#3498db';

    return (
        <Tooltip title={isObj && jm.descr ? `${nom} - ${jm.descr}` : nom}>
            <motion.div
                whileHover={{ scale: 1.2, y: -5 }}
                style={{
                    width: size,
                    height: size,
                    borderRadius: '50%',
                    background: 'rgba(255,255,255,0.9)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    border: `2px solid ${color}`,
                    boxShadow: glow ? `0 0 15px ${color}` : `0 2px 5px rgba(0,0,0,0.1)`,
                    overflow: 'hidden',
                    cursor: 'pointer',
                    position: 'relative'
                }}
            >
                <img src={finalSrc} alt={nom} style={{ width: '80%', height: '80%', objectFit: 'contain' }} />
                {rarity === 'legendary' && <FaStar style={{ position: 'absolute', top: 2, right: 2, color: 'gold', fontSize: size * 0.25 }} />}
            </motion.div>
        </Tooltip>
    );
};


const PlayerCard = ({ user, rank, onAvatarClick, rarityMap = {} }: { user: any, rank: number, onAvatarClick: (u: any) => void, rarityMap?: Record<string, string> }) => {
    const isChampion = rank === 1;
    const isElite = rank > 1 && rank <= 5;

    // Check if the user has any legendary Javamon
    const hasLegendary = user.javamons && user.javamons.some((jm: any) => (typeof jm === 'object' && jm.rarity === 'legendary'));

    let bannerColor = isChampion ? bgGradients.champion : isElite ? bgGradients.elite : bgGradients.challenger;

    // Si no es el campeón pero tiene un legendario, le damos una apariencia especial (Color Morado Brillante)
    if (!isChampion && hasLegendary) {
        bannerColor = bgGradients.legendary;
    }

    const isDarkText = bannerColor === bgGradients.challenger;
    const textColor = isDarkText ? '#333' : '#fff';

    return (
        <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: rank * 0.05 }}
            style={{
                background: '#fff',
                borderRadius: 20,
                overflow: 'hidden',
                boxShadow: isChampion ? '0 10px 30px rgba(255, 215, 0, 0.4)' : hasLegendary ? '0 6px 20px rgba(155, 89, 182, 0.4)' : '0 4px 15px rgba(0,0,0,0.08)',
                border: isChampion ? '4px solid #ffd700' : hasLegendary ? '2px solid #9b59b6' : isElite ? '2px solid #ccc' : '1px solid #eee',
                display: 'flex',
                flexDirection: isChampion ? 'row' : 'column',
                height: '100%',
                position: 'relative'
            }}
        >
            {/* Header / Avatar Strip */}
            <div style={{
                background: bannerColor,
                padding: isChampion ? '30px' : '20px',
                display: 'flex',
                flexDirection: isChampion ? 'column' : 'row',
                alignItems: 'center',
                justifyContent: isChampion ? 'center' : 'flex-start',
                gap: 15,
                color: textColor,
                minWidth: isChampion ? '30%' : 'auto'
            }}>
                <div style={{ position: 'relative' }}>
                    <img
                        src={user.avatar}
                        alt="avatar"
                        onClick={() => onAvatarClick(user)}
                        style={{
                            width: isChampion ? 120 : 60,
                            height: isChampion ? 120 : 60,
                            borderRadius: '50%',
                            border: `4px solid ${textColor}`,
                            objectFit: 'cover',
                            cursor: 'pointer',
                            boxShadow: '0 4px 10px rgba(0,0,0,0.2)'
                        }}
                    />
                    {isChampion && <div style={{ position: 'absolute', top: -20, left: '50%', transform: 'translateX(-50%)', fontSize: 40 }}><FaCrown style={{ color: '#fff', filter: 'drop-shadow(0 2px 2px rgba(0,0,0,0.3))' }} /></div>}
                    {!isChampion && <div style={{ position: 'absolute', bottom: -10, left: '50%', transform: 'translateX(-50%)', background: '#333', color: '#fff', padding: '2px 8px', borderRadius: 10, fontSize: 12, fontWeight: 'bold', border: `1px solid ${textColor}` }}>#{rank}</div>}
                </div>

                <div style={{ textAlign: isChampion ? 'center' : 'left', flex: 1 }}>
                    {isChampion && <div style={{ fontSize: 14, fontWeight: 'bold', letterSpacing: 3, opacity: 0.9, textTransform: 'uppercase' }}>Grand Master</div>}
                    {/* Badge destacado si el jugador tiene un Legendario */}
                    {!isChampion && hasLegendary && <div style={{ fontSize: 11, fontWeight: 'bold', letterSpacing: 1, opacity: 0.9, textTransform: 'uppercase', color: '#f1c40f' }}><FaStar /> Portador Legendario</div>}
                    <div style={{ fontSize: isChampion ? 28 : (hasLegendary ? 20 : 18), fontWeight: 900, marginBottom: 5 }}>{user.nom}</div>
                    <div style={{ display: 'flex', gap: 10, justifyContent: isChampion ? 'center' : 'flex-start', flexWrap: 'wrap' }}>
                        <Badge count={`${user.javamons?.length || 0} Capturas`} style={{ backgroundColor: '#ff4d4f', fontWeight: 'bold', boxShadow: '0 2px 4px rgba(0,0,0,0.2)' }} />
                        <Badge count={`${user.punts || 0} PTS`} style={{ backgroundColor: '#1890ff', fontWeight: 'bold', boxShadow: '0 2px 4px rgba(0,0,0,0.2)' }} />
                    </div>
                </div>
            </div>

            {/* Javamon Team Display */}
            <div style={{
                padding: '20px',
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                background: '#fff'
            }}>
                <div style={{ fontSize: 14, fontWeight: 'bold', color: '#888', marginBottom: 15, textAlign: 'center', textTransform: 'uppercase', letterSpacing: 1 }}>
                    <BiTrophy style={{ marginRight: 5, color: '#f1c40f' }} />
                    Equipo Javamon
                </div>

                {user.javamons && user.javamons.length > 0 ? (
                    <div style={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        gap: isChampion ? 15 : 10,
                        justifyContent: 'center',
                        alignItems: 'center'
                    }}>
                        {user.javamons.map((jm: any, idx: number) => (
                            <JavamonSprite key={idx} jm={jm} size={isChampion ? 70 : 50} glow={isChampion || (typeof jm === 'object' && jm.rarity === 'legendary')} rarityMap={rarityMap} />
                        ))}
                    </div>
                ) : (
                    <div style={{ textAlign: 'center', color: '#aaa', fontStyle: 'italic', padding: '20px 0' }}>
                        Aún no ha capturado ningún Javamon...
                    </div>
                )}
            </div>

            {/* Rank Watermark */}
            {isChampion && <div style={{ position: 'absolute', right: -20, bottom: -20, fontSize: 150, opacity: 0.05, pointerEvents: 'none' }}>#1</div>}
        </motion.div>
    );
};


const Ranking = () => {
    const { isLoggedIn, userId, user, setBreadCrumb } = useContext(AuthContext);
    const [modalVisible, setModalVisible] = useState(false);
    const [simulatedUserId, setSimulatedUserId] = useState<string | undefined>(undefined);

    // Solo los administradores/profesores (tipus 0 y 1) pueden ver el Ranking General
    // y configurar qué aulas se incluyen en él.
    const isTeacher = user.tipus === "0" || user.tipus === "1";

    const [dades, setDades] = useState<any[]>([]);
    const [allRankingData, setAllRankingData] = useState<any[]>([]);
    const [aulas, setAulas] = useState<{ id: string; nom: string }[]>([]);
    // Los profesores arrancan en el Ranking General; los alumnos, sin selección
    // hasta que se carguen sus aulas (se selecciona la primera automáticamente).
    const [selectedAula, setSelectedAula] = useState<string>(isTeacher ? 'general' : '');
    const [aulasAmbJoc, setAulasAmbJoc] = useState<number[]>([]);
    // Aulas que el profesor elige incluir en el Ranking General. Si está vacío,
    // se usan por defecto las aulas con Joc Actiu (aulasAmbJoc).
    const [generalAulas, setGeneralAulas] = useState<string[]>([]);
    const [loading, setLoading] = useState(false);
    const [pantalla, setPantalla] = useState(0);
    const [rarityMap, setRarityMap] = useState<Record<string, string>>({});

    // Fetch logic prioritizing Javamons caught, then points
    useEffect(() => {
        let mounted = true;
        async function fetchData() {
            if (!isLoggedIn) return;
            setLoading(true);

            try {
                // Fetch classrooms
                let classrooms: { id: string, nom: string }[] = [];
                if (user.tipus === "0" || user.tipus === "1") {
                    const data: any[] = await GetUsuarisIdJutgeNotes(userId);
                    const uniqueAulasMap = new Map();
                    data.flatMap((student: any) => student.practiques || []).forEach((p: any) => {
                        if (p.id_aula && p.aula) uniqueAulasMap.set(p.id_aula.toString(), p.aula);
                    });
                    classrooms = Array.from(uniqueAulasMap.entries()).map(([id, nom]) => ({ id, nom }));
                } else {
                    const al = await GetAlumneId(userId);
                    if (al?.aules) {
                        classrooms = al.aules.map((a: any) => ({ id: (a.id || a.id_aula).toString(), nom: a.nom || `Aula ${a.id || a.id_aula}` }));
                    }
                }
                if (mounted) {
                    setAulas(classrooms);
                    // Los alumnos no pueden ver el Ranking General: si no tienen aula
                    // seleccionada (o quedó apuntando a 'general'), seleccionamos la primera.
                    if (!isTeacher && classrooms.length > 0) {
                        setSelectedAula(prev => (prev && prev !== 'general') ? prev : classrooms[0].id);
                    }
                }

                // Fetch Joc Actiu
                const activities = await GetSceneActivities();
                let ambJoc: number[] = [];
                if (activities && activities.length > 0) {
                    ambJoc = Array.from(new Set(activities.map((a: any) => a.id_aula))) as number[];
                }
                if (mounted) setAulasAmbJoc(ambJoc);

                const cfgData = await GetJavamonConfig();
                if (cfgData) {
                    const rList = cfgData.rarities && cfgData.rarities !== "null" ? JSON.parse(cfgData.rarities) : [];
                    const rMap: Record<string, string> = {};
                    rList.forEach((r: any) => {
                        const n = r.nom?.toLowerCase() || '';
                        rMap[n] = r.color;
                        rMap[r.nom] = r.color;
                        if (n === 'épico' || n === 'epico') rMap['epic'] = r.color;
                        if (n === 'legendario' || n === 'leyenda') rMap['legendary'] = r.color;
                        if (n === 'normal' || n === 'comun' || n === 'común') rMap['normal'] = r.color;
                    });
                    if (mounted) setRarityMap(rMap);
                }

                const data = await GetRankingsUsuariId(userId + "");
                if (mounted && data) {
                    const rankingData = Array.isArray(data) ? data : data.ranking;
                    // Images mapping removed; using getJavamonImage instead for performance.
                    setAllRankingData(rankingData || []);
                }
            } catch (err) {
                console.error("Error cargando configuracion y ranking:", err);
            }
            if (mounted) setLoading(false);
        }
        fetchData();
        return () => { mounted = false; };
    }, [isLoggedIn, userId, user]);

    // Lógica para filtrar por aula y ordenar el ranking localmente
    useEffect(() => {
        let filtered = [...allRankingData];

        if (selectedAula === 'general') {
            // El Ranking General es exclusivo de profesores/admins.
            if (!isTeacher) {
                setDades([]);
                return;
            }
            // Aulas que definen el Ranking General: las que el profesor haya
            // configurado explícitamente, o (si no hay configuración) las que
            // tienen Joc Actiu.
            const generalAulaIds = generalAulas.length > 0
                ? generalAulas.map(id => parseInt(id))
                : aulasAmbJoc;
            filtered = filtered.filter(u => {
                if (!u.aules) return false;
                return u.aules.some((aulaId: number) => generalAulaIds.includes(aulaId));
            });
        } else {
            // Filtrado por aula específica
            const aulaId = parseInt(selectedAula);
            filtered = filtered.filter(u => {
                if (!u.aules) return false;
                return u.aules.includes(aulaId);
            });
        }

        const ordered = filtered.sort((a, b) => {
            const aLen = a.javamons?.length || 0;
            const bLen = b.javamons?.length || 0;
            if (bLen !== aLen) return bLen - aLen; // Priorize quantity of Javamons
            return (b.punts || 0) - (a.punts || 0); // Desempate por puntos
        });
        setDades(ordered);
    }, [selectedAula, allRankingData, aulasAmbJoc, generalAulas, isTeacher]);

    const handleAvatarClick = (row: any) => {
        setSimulatedUserId(row.id_usuari);
        setModalVisible(true);
    };

    async function checkDades() {
        setLoading(true);
        const data = await GetRankingsUsuariId(userId + "");
        setBreadCrumb([
            { nom: 'Home', nav: '/home' },
            { nom: 'jutge', nav: '/jutge' },
            { nom: 'ranking', nav: `/jutge/ranking` }
        ], true);
        
        let rankingData = [];
        if (data) {
             rankingData = Array.isArray(data) ? data : data.ranking;
        }

        setAllRankingData(rankingData || []);
        setLoading(false);
    }

    const champion = dades[0];
    const elites = dades.slice(1, 5);
    const challengers = dades.slice(5);

    return (
        <>
            <AppSider currentVal={4} setPantalla={setPantalla} />

            <Content style={{ background: '#f0f2f5', padding: '20px', minHeight: '100vh', fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif" }}>
                <div style={{ textAlign: 'center', marginBottom: 40, paddingTop: 20 }}>
                    <motion.div
                        initial={{ scale: 0.8, opacity: 0 }}
                        animate={{ scale: 1, opacity: 1 }}
                        transition={{ type: 'spring', bounce: 0.5 }}
                    >
                        <FaFireAlt style={{ fontSize: 45, color: '#ff4d4f', marginBottom: 10, filter: 'drop-shadow(0 2px 4px rgba(255,77,79,0.4))' }} />
                        <h1 style={{ fontSize: '3.5rem', fontWeight: 900, color: '#1f2937', textShadow: '0 2px 10px rgba(0,0,0,0.1)', margin: 0, textTransform: 'uppercase', letterSpacing: 2 }}>
                            Javamon Master League
                        </h1>
                        <p style={{ fontSize: '1.2rem', color: '#64748b', marginTop: 10, letterSpacing: 1, fontWeight: 'bold' }}>
                            Clasificación Oficial de Cazadores. El equipo más grande domina.
                        </p>
                    </motion.div>

                    <div style={{ marginTop: 25 }}>
                        <Select
                            value={selectedAula || undefined}
                            placeholder="Selecciona un aula"
                            onChange={(val) => setSelectedAula(val)}
                            style={{ width: 350, fontSize: '1.1rem', textAlign: 'left' }}
                            size="large"
                        >
                            {/* El Ranking General solo está disponible para profesores/admins */}
                            {isTeacher && (
                                <Select.Option value="general">
                                    <FaStar style={{ color: '#f1c40f', marginRight: 8 }} /> Ranking General
                                </Select.Option>
                            )}
                            {aulas.map(a => (
                                <Select.Option key={a.id} value={a.id}>
                                    Aula: {a.nom}
                                </Select.Option>
                            ))}
                        </Select>
                    </div>

                    {/* Configurador de aulas del Ranking General (solo profesores/admins) */}
                    {isTeacher && selectedAula === 'general' && (
                        <div style={{ marginTop: 15 }}>
                            <Select
                                mode="multiple"
                                allowClear
                                value={generalAulas}
                                onChange={(vals) => setGeneralAulas(vals)}
                                placeholder="Configura las aulas del Ranking General (por defecto: aulas con Joc Actiu)"
                                style={{ width: 500, maxWidth: '90%', textAlign: 'left' }}
                                size="middle"
                                maxTagCount="responsive"
                            >
                                {aulas.map(a => (
                                    <Select.Option key={a.id} value={a.id}>
                                        {a.nom}
                                    </Select.Option>
                                ))}
                            </Select>
                            <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 6 }}>
                                {generalAulas.length > 0
                                    ? `Mostrando ${generalAulas.length} aula(s) seleccionada(s).`
                                    : 'Sin selección: se muestran las aulas con Joc Actiu.'}
                            </div>
                        </div>
                    )}
                </div>

                <div style={{ maxWidth: 1400, margin: '0 auto' }}>
                    {/* THE CHAMPION */}
                    {champion && (
                        <div style={{ marginBottom: 40 }}>
                            <PlayerCard user={champion} rank={1} onAvatarClick={handleAvatarClick} rarityMap={rarityMap} />
                        </div>
                    )}

                    {/* THE ELITE FOUR (Ranks 2 to 5) */}
                    {elites.length > 0 && (
                        <>
                            <h2 style={{ color: '#1e293b', borderBottom: '3px solid #cbd5e1', paddingBottom: 10, marginBottom: 20, display: 'flex', alignItems: 'center' }}>
                                <FaStar style={{ color: '#f1c40f', marginRight: 10 }} /> La Élite
                            </h2>
                            <Row gutter={[20, 20]}>
                                {elites.map((user, idx) => (
                                    <Col xs={24} lg={12} key={user.id_usuari}>
                                        <PlayerCard user={user} rank={idx + 2} onAvatarClick={handleAvatarClick} rarityMap={rarityMap} />
                                    </Col>
                                ))}
                            </Row>
                        </>
                    )}

                    {/* CHALLENGERS (Rank 6+) */}
                    {challengers.length > 0 && (
                        <div style={{ marginTop: 50 }}>
                            <h2 style={{ color: '#64748b', borderBottom: '3px solid #e2e8f0', paddingBottom: 10, marginBottom: 20 }}>
                                Aspirantes
                            </h2>
                            <Row gutter={[20, 20]}>
                                {challengers.map((user, idx) => (
                                    <Col xs={24} md={12} xl={8} key={user.id_usuari}>
                                        <PlayerCard user={user} rank={idx + 6} onAvatarClick={handleAvatarClick} rarityMap={rarityMap} />
                                    </Col>
                                ))}
                            </Row>
                        </div>
                    )}
                </div>

                <Modal
                    title="Mochila de Cazador"
                    open={modalVisible}
                    onCancel={() => setModalVisible(false)}
                    footer={null}
                    width={1000}
                    bodyStyle={{ padding: 0 }}
                >
                    <Mochila user_id_simulado={simulatedUserId} />
                </Modal>
            </Content>

            {(user.tipus === "1" || user.tipus === "0") && !loading && (
                <FloatButton
                    shape="circle"
                    onClick={checkDades}
                    style={{ insetInlineStart: 97, backgroundColor: AvaluacioColors.colors.color6 }}
                    icon={<BiRefresh />}
                />
            )}
        </>
    );
};

export default Ranking;
